import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import FilterListIcon from '@mui/icons-material/FilterList';
import PeopleIcon from '@mui/icons-material/People';
import WarningIcon from '@mui/icons-material/Warning';
import queryString from 'query-string';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { OrderBy } from '../../common/types';
import { USER_LIST_FILTERS } from '../../common/model/ui/user-list-filters';
import { getListRange } from '../../common/utils/services/order-list-utils';
import { getUserListSortOptions } from '../../common/utils/services/user-list-utils';
import { useListQueryParams } from '../../hooks/use-list-query-params';
import { useUserListFilters } from '../../hooks/use-user-list-filters';
import UserService from '../../services/user.service';
import OvCompactButton from '../UI/atoms/OvCompactButton';
import OvSearchInput from '../UI/atoms/OvSearchInput';
import OvListSortSelect from '../UI/molecules/OvListSortSelect';
import OvListStateMessage from '../UI/molecules/OvListStateMessage';
import OvListFilterBar from '../UI/organisms/OvListFilterBar';
import OvRecordListPage from '../UI/organisms/OvRecordListPage';
import OvUserList from '../UI/organisms/OvUserList';

export const ADMIN_USER_LIST_LIMIT = 10;

const DEFAULT_USER_LIST_ORDER: OrderBy = {
  order_by: 'registration_date',
  order_dir: 'desc',
};

const AdminUserList: FC = () => {
  const { t } = useTranslation();
  const {
    offset,
    orderBy,
    filters,
    filterQueryParams,
    hasAppliedFilters,
    changeFilter,
    clearFilters,
    changeOrderBy,
    setOffset,
  } = useListQueryParams(USER_LIST_FILTERS, DEFAULT_USER_LIST_ORDER);
  const filtersWithOptions = useUserListFilters(filters);
  const [searchText, setSearchText] = useQueryParam(
    'search_text',
    withDefault(StringParam, '')
  );

  const query = queryString.stringify({
    limit: ADMIN_USER_LIST_LIMIT,
    offset,
    order_by: orderBy?.order_by,
    order_dir: orderBy?.order_dir,
    ...filterQueryParams,
    search_text: searchText
      ? searchText.toLowerCase().split(' ').join(',')
      : undefined,
  });

  const {
    data: paginatedUsers,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['admin-users', query],
    queryFn: () => UserService.loadUsersByQuery(query),
    retry: 3,
    placeholderData: keepPreviousData,
  });

  const users = paginatedUsers?.docs ?? [];
  const total = paginatedUsers?.total ?? 0;
  const hasNarrowedList = hasAppliedFilters || !!searchText;

  const onSearchChange = (value: string) => {
    setOffset(0);
    setSearchText(value.toLowerCase() || null);
  };

  const onClear = () => {
    clearFilters();
    setSearchText(null);
  };

  const getResultSummary = () => {
    if (isError) {
      return t('common.listResults.unavailable');
    }

    if (isPending) {
      return t('common.listResults.loading');
    }

    return t('userList.resultRange', {
      ...getListRange(offset, ADMIN_USER_LIST_LIMIT, total),
      total,
    });
  };

  const renderContent = () => {
    if (isError) {
      return (
        <OvListStateMessage
          isAlert
          icon={<WarningIcon />}
          title={t('userList.states.errorTitle')}
          description={t('userList.states.errorDescription')}
        >
          <OvCompactButton onClick={() => refetch()}>
            {t('common.actions.retry')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    if (isPending) {
      return null;
    }

    if (users.length > 0) {
      return (
        <OvUserList
          users={users}
          orderBy={orderBy?.order_by ?? ''}
          orderDir={orderBy?.order_dir ?? ''}
          onSort={changeOrderBy}
        />
      );
    }

    if (hasNarrowedList) {
      return (
        <OvListStateMessage
          icon={<FilterListIcon />}
          title={t('userList.states.noResultsTitle')}
          description={t('userList.states.noResultsDescription')}
        >
          <OvCompactButton onClick={onClear}>
            {t('common.actions.clearFilters')}
          </OvCompactButton>
        </OvListStateMessage>
      );
    }

    return (
      <OvListStateMessage
        icon={<PeopleIcon />}
        title={t('userList.states.emptyTitle')}
        description={t('userList.states.emptyDescription')}
      />
    );
  };

  return (
    <OvRecordListPage
      title={t('common.users')}
      icon={<PeopleIcon />}
      headerAction={
        <OvSearchInput
          placeholder={t('common.actions.search')}
          initialValue={searchText}
          onSearchChange={onSearchChange}
        />
      }
      filters={
        <OvListFilterBar
          filters={filtersWithOptions}
          onFilterChange={changeFilter}
          onClearFilters={clearFilters}
        >
          <OvListSortSelect
            options={getUserListSortOptions(t)}
            orderBy={orderBy?.order_by ?? ''}
            orderDir={orderBy?.order_dir ?? ''}
            onChange={changeOrderBy}
          />
        </OvListFilterBar>
      }
      resultSummary={getResultSummary()}
      total={total}
      limit={ADMIN_USER_LIST_LIMIT}
      offset={offset}
      onPageChange={(_, page) => setOffset((page - 1) * ADMIN_USER_LIST_LIMIT)}
      isBusy={isFetching}
    >
      {renderContent()}
    </OvRecordListPage>
  );
};

export default AdminUserList;
